import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AxiosService } from 'src/axios/axios.service';
import { useIntegrationsContext } from 'src/providers/IntegrationsProvider/Integrations.context';
import { eIntegrationStatus } from 'src/providers/IntegrationsProvider/Integrations.enums';
import { useSubscriptionContext } from 'src/providers/SubscriptionProvider/Subscription.context';
import { canSeeWapiDisconnectedBanner } from 'src/providers/SubscriptionProvider/Subscription.utils';
import { useWorkspaceContext } from 'src/providers/WorkspaceProvider/Workspace.context';

type tWapiAccountStatusResponse = { status?: string };

export default function WapiDisconnectedBanner() {
  const { t } = useTranslation('common/wapiDisconnectedBanner');
  const { whatsappBusinessIntegration, isHydrated } = useIntegrationsContext();
  const { currentWorkspaceId } = useWorkspaceContext();
  const { subscription } = useSubscriptionContext();
  const [accountStatus, setAccountStatus] = useState<string | null>(null);

  const isDisconnected =
    isHydrated &&
    canSeeWapiDisconnectedBanner(subscription) &&
    whatsappBusinessIntegration.state === eIntegrationStatus.DISCONNECTED;

  useEffect(() => {
    if (!isDisconnected) {
      setAccountStatus(null);
      return;
    }
    if (!currentWorkspaceId) return;

    const controller = new AbortController();
    AxiosService.get<tWapiAccountStatusResponse>(
      `/whatsapp-api/account/${currentWorkspaceId}`,
      { isLegacy: true, signal: controller.signal },
    )
      .then((response) => {
        if (response?.status) setAccountStatus(response.status);
      })
      .catch((e: unknown) => {
        if ((e as { name?: string })?.name !== 'AbortError') {
          console.error('WapiDisconnectedBanner: failed to fetch status', e);
        }
      });
    return () => controller.abort();
  }, [isDisconnected, currentWorkspaceId]);

  if (!isDisconnected) return null;

  return (
    <div className="flex items-start gap-3 w-full text-sm bg-red-50 border-l-4 border-red-400 rounded-r-md px-4 py-3 text-red-700">
      <ExclamationTriangleIcon className="w-5 h-5 shrink-0 mt-0.5" />
      <div>
        <p className="font-semibold">{t('title')}</p>
        <p>{t('description')}</p>
        {accountStatus && (
          <p className="mt-1">
            <span className="font-semibold">{t('reasonLabel')}: </span>
            {t(`status.${accountStatus.toLowerCase()}`, { defaultValue: t('status.unknown') })}
          </p>
        )}
      </div>
    </div>
  );
}
